import { LOGIN, REGISTRATION } from 'apps/promo/constants/UrlConstants';
import { INITIAL_PAGE_PARAM, UTM_PARAM_NAMES } from 'apps/promo/constants/utmParams';

const AUTH_ENTRY_PATHS = [LOGIN, REGISTRATION];

const isRelativeUrl = (url: string): boolean => url.startsWith('/') && !url.startsWith('//');

const isAuthEntryUrl = (pathname: string): boolean => {
    const normalizedPathname = pathname.toLowerCase().replace(/\/+$/, '');

    return AUTH_ENTRY_PATHS.some((path) => normalizedPathname === path || normalizedPathname.endsWith(path));
};

const isPaysendUrl = (parsedUrl: URL): boolean =>
    (parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') &&
    (parsedUrl.hostname === 'paysend.com' || parsedUrl.hostname.endsWith('.paysend.com'));

/**
 * Relative urls stay relative, absolute paysend urls keep their origin. Anything
 * else (foreign host, another scheme, `//host/path` that would need a real protocol
 * to resolve) is rejected, so the caller returns it untouched.
 */
const parseAuthUrl = (url: string): { parsedUrl: URL; isRelative: boolean } | null => {
    try {
        if (isRelativeUrl(url)) {
            return { parsedUrl: new URL(url, 'http://localhost'), isRelative: true };
        }

        const parsedUrl = new URL(url);

        return isPaysendUrl(parsedUrl) ? { parsedUrl, isRelative: false } : null;
    } catch {
        return null;
    }
};

export const getUtmParams = (search: URLSearchParams): [string, string][] =>
    UTM_PARAM_NAMES.map((name) => [name, search.get(name)] as [string, string | null]).filter(
        (entry): entry is [string, string] => entry[1] !== null && entry[1] !== '',
    );

const CALCULATOR_OWNED_PARAM_NAMES = ['send', 'receive'];

/**
 * Redirect url от Strapi несёт только состояние калькулятора, так что все
 * остальные параметры текущей страницы (utm, promocode, ...) при переключении
 * калькулятора терялись бы. Переносим их обратно, приоритет — у redirect url.
 */
export const keepCurrentQueryParams = (url: string, search: string): string => {
    if (!url || !search) {
        return url;
    }

    const parsed = parseAuthUrl(url);
    if (!parsed) {
        return url;
    }

    const { parsedUrl, isRelative } = parsed;
    const currentParams = new URLSearchParams(search);
    const namesToKeep = [...new Set(currentParams.keys())].filter((name) => !parsedUrl.searchParams.has(name) && !CALCULATOR_OWNED_PARAM_NAMES.includes(name));

    namesToKeep.forEach((name) => currentParams.getAll(name).forEach((value) => parsedUrl.searchParams.append(name, value)));

    return isRelative ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` : parsedUrl.toString();
};

/**
 * Utm params only enter the login/registration urls, they are read from the cookies
 * everywhere else. `initial_page` instead rides along every internal link, so the
 * page the visit started from keeps travelling with the user across the landings
 * and still reaches the auth pages when the cookie is not available.
 */
export const addTrackingParamsToUrl = (url: string | undefined | null, search: URLSearchParams, initialPage?: string | null): string | undefined | null => {
    if (!url) {
        return url;
    }

    const parsed = parseAuthUrl(url);
    if (!parsed) {
        return url;
    }

    const { parsedUrl, isRelative } = parsed;
    const utmParams = isAuthEntryUrl(parsedUrl.pathname) ? getUtmParams(search) : [];
    if (utmParams.length === 0 && !initialPage) {
        return url;
    }

    utmParams.forEach(([name, value]) => parsedUrl.searchParams.set(name, value));
    if (initialPage) {
        parsedUrl.searchParams.set(INITIAL_PAGE_PARAM, initialPage);
    }

    return isRelative ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` : parsedUrl.toString();
};
